import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  IconButton,
  Button,
  TablePagination,
  styled,
  tableCellClasses,
  Box,
  Skeleton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Add, Edit, Visibility, FileCopy } from "@mui/icons-material";
import { axiosAPI } from "../../../Components/AxiosComponents/Axios";
import { encryptId } from "../../../Components/CryptoComponent";
import {
  errorNotify,
  successNotify,
} from "../../../Components/ToastComponent/ToastNotification";
import { useStaffLayout } from "../../../Components/Context/CandidateLayoutContext";
import TextAreaInput from "../../../Components/InputComponents/TextAreaInput";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const RecruitmentProcedureListNew = ({ isAdmin }) => {
  const navigate = useNavigate();

  const { privilegeData } = useStaffLayout();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const [recruitmentProcedureList, setRecruitmentProcedureList] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loaderOpen, setLoaderOpen] = useState(true);

  function hasRecProcAddprivilege(privilegeData) {
    if (isAdmin) return true;
    return privilegeData.some((subModule) => {
      const isJob = subModule.subModuleName
        .toLowerCase()
        .includes("recruitment procedure");

      if (!isJob) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() === "add" && priv.assigned == true
      );
    });
  }

  function hasRecProcEditprivilege(privilegeData) {
    if (isAdmin) return true;
    return privilegeData.some((subModule) => {
      const isJob = subModule.subModuleName
        .toLowerCase()
        .includes("recruitment procedure");
      if (!isJob) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() === "edit" && priv.assigned == true
      );
    });
  }

  function hasRecProcViewprivilege(privilegeData) {
    if (isAdmin) return true;
    return privilegeData.some((subModule) => {
      const isJob = subModule.subModuleName
        .toLowerCase()
        .includes("recruitment procedure");
      if (!isJob) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() === "view" && priv.assigned == true
      );
    });
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getData = () => {
    axiosAPI
      ?.get(`/RecruitmentProcedure/list`)
      .then((res) => {
        setRecruitmentProcedureList(res?.data?.data);
        setLoaderOpen(false);
      })
      .catch((err) => {
        errorNotify(`Something went wrong`);
      });
  };

  const handleStatusChange = (c, e) => {
    axiosAPI
      ?.post(`/RecruitmentProcedure/statusChange`, {
        ProcedureId: c?.RP_Id,
        status: e.target.value,
        ProcedureName: c?.RP_Name,
      })
      .then((res) => {
        if (res?.data?.success) {
          successNotify(res?.data?.data?.message);
          getData();
        } else {
          errorNotify(res?.data?.data?.message);
        }
      })
      .catch((err) => {});
  };

  const getActions = (row) => {
    if (row.RP_IsActive !== 1) {
      // Only View button when inactive
      return [
        hasRecProcViewprivilege(privilegeData) && {
          icon: Visibility,
          handler: () => {
            const encrypted = encodeURIComponent(encryptId(row?.RP_Id));
            navigate(`/RecruitmentProcedure/View/${encrypted}`);
          },
          color: "primary",
          tooltip: "View Recruitment Procedure",
        },
      ].filter(Boolean);
    }

    // Full actions when active
    return [
      hasRecProcViewprivilege(privilegeData) && {
        icon: Visibility,
        handler: () => {
          const encrypted = encodeURIComponent(encryptId(row?.RP_Id));
          navigate(`/RecruitmentProcedure/View/${encrypted}`);
        },
        color: "primary",
        tooltip: "View Recruitment Procedure",
      },
      {
        icon: Edit,
        handler: () => {
          const encrypted = encodeURIComponent(encryptId(row?.RP_Id));
          navigate(`/RecruitmentProcedure/Edit/${encrypted}`);
        },
        color: "warning",
        tooltip: "Edit this Recruitment Procedure",
      },
      hasRecProcEditprivilege(privilegeData) && {
        icon: FileCopy,
        handler: () => {
          const encrypted = encodeURIComponent(encryptId(row?.RP_Id));
          navigate(`/RecruitmentProcedure/Clone/${encrypted}`);
        },
        tooltip: "Clone this Recruitment Procedure",
      },
    ].filter(Boolean);
  };
  const navigateToView = (row)=>{
    const encrypted = encodeURIComponent(encryptId(row?.RP_Id));
    navigate(`/RecruitmentProcedure/Edit/${encrypted}`);
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    getData();
  }, [page, rowsPerPage]);

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        >
          Recruitment Procedure
        </Typography>

        {/* Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            ml: "auto",
          }}
        >
          {hasRecProcAddprivilege(privilegeData) && (
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{ background: "#E75E1F" }}
              onClick={() => {
                navigate(`/RecruitmentProcedure/Add`);
              }}
              disabled={!hasRecProcAddprivilege(privilegeData)}
            >
              Add
            </Button>
          )}
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          border: "1px solid lightgray",
          borderRadius: "10px",
          maxWidth: "100vw",
          overflowX: "auto",
          margin: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: "50px" }}>#</StyledTableCell>
              <StyledTableCell>Recruitment Procedure</StyledTableCell>
              <StyledTableCell sx={{ width: "350px" }}>Status</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: "150px" }}>
                Actions
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loaderOpen ? (
              Array.from({ length: 7 }).map((_, idx) => (
                <TableRow key={idx}>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <StyledTableCell key={index}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={40}
                      />
                    </StyledTableCell>
                  ))}
                </TableRow>
              ))
            ) : recruitmentProcedureList &&
              recruitmentProcedureList?.length > 0 ? (
              recruitmentProcedureList
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((c, index) => (
                  <StyledTableRow key={c.RP_Id}>
                    <StyledTableCell>
                      {(page + 1) * rowsPerPage - (rowsPerPage - (index + 1))}
                    </StyledTableCell>
                    <StyledTableCell sx={{cursor:"pointer"}} onClick={()=>{navigateToView(c)}}>{c?.RP_Name}</StyledTableCell>
                    <StyledTableCell>
                      <Box>
                        <ToggleButtonGroup
                          value={c?.RP_IsActive}
                          exclusive
                          onChange={(e, newValue) => {
                            if (
                              newValue !== null &&
                              newValue !== c?.RP_IsActive
                            ) {
                              setPendingStatus(newValue);
                              setSelectedRow(c);
                              setConfirmOpen(true);
                            }
                          }}
                          size="small"
                          sx={{ borderRadius: 2 }}
                        >
                          <ToggleButton
                            value={1}
                            sx={{
                              "&.Mui-selected": {
                                backgroundColor: "#0f9713ff",
                                color: "white",
                                transition: "all 0.3s ease-in-out",
                                "&:hover": { backgroundColor: "green" },
                              },
                            }}
                          >
                            Active
                          </ToggleButton>
                          <ToggleButton
                            value={0}
                            sx={{
                              "&.Mui-selected": {
                                backgroundColor: "#F44336",
                                color: "white",
                                transition: "all 0.3s ease-in-out",
                                "&:hover": { backgroundColor: "red" },
                              },
                            }}
                          >
                            Inactive
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      {getActions(c).map((action, idx) => (
                        <Tooltip key={idx} title={action.tooltip || ""}>
                          <IconButton
                            color={action.color || "default"}
                            size="small"
                            onClick={() => action.handler(c)}
                          >
                            <action.icon/>
                          </IconButton>
                        </Tooltip>
                      ))}
                    </StyledTableCell>
                  </StyledTableRow>
                ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={9} align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      py: 2,
                      fontSize: "20px",
                      fontFamily: "Geist, sans-serif",
                    }}
                  >
                    No data available
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirmation Modal */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle
          sx={{
            fontFamily: "Geist, sans-serif",
            fontWeight: 600,
            fontSize: "1rem",
          }}
        >
          Confirm Status Change
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                mb: 1,
                fontFamily: "Geist, sans-serif",
              }}
            >
              Are you sure you want to{" "}
              {pendingStatus === 1 ? "enable" : "disable"} Active Status?
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            sx={{ fontFamily: "Geist, sans-serif" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleStatusChange(selectedRow, {
                target: { value: pendingStatus },
              });
              setConfirmOpen(false);
            }}
            variant="contained"
            
            sx={{ fontFamily: "Geist, sans-serif" }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={recruitmentProcedureList?.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          // General font size for the pagination root
          fontSize: { xs: "12px", sm: "14px" },
          // Specific font size for the "Rows per page" label
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif", // Optional
          },
        }}
      />
    </Paper>
  );
};

export default RecruitmentProcedureListNew;
